<template>
  <TkdServiceDevice
    v-model:current-device="device"
    v-model:current-device-event="deviceEvent"
    v-model:installation-point="installationPoint"
    v-model:tkd-service="TKDService"
    v-model:new-device="TKDDevice"
    v-model:new-battery-pack="newBatteryPack"
    v-model:accessory-service-list="accessoryServiceList"
    v-model:images="images"
    :suggested-ticket-numbers="suggestedTicketNumbers"
    :show-report="showReport"
    :old-device-serial-number="deviceSerialNumber"
    @on-save="onSave"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, provide, ref } from 'vue';
import { useDeviceForm } from '@/composables/useDeviceForm';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { DeviceClasses } from '@/models/devices/Device';
import TkdServiceDevice from '@/components/Forms/TkdServiceComponents/TkdServiceDevice.vue';
import { useTKDService } from '@/composables/useTKDService';
import { useImageCapture } from '@/composables/useImageCapture';
import { Image } from '@/models/Image';
import { useOrderStore } from '@/store/order/orderStore';
import { useFormStore } from '@/store/form/formStore';

const props = defineProps<{
  node?: InstallationPoint;
  parentId: string;
  payload?: any;
}>();
const emit = defineEmits(['update-entity', 'update-entities']);

const {
  device,
  installationPoint,
  deviceEvent,
  failedInstallationAttempt,
  initDevice,
  initAccessories,
  initInstallationPoint,
  createDevice: createNewDevice,
  updateDevice: updateNewDevice,
} = useDeviceForm(emit);

const {
  TKDService,
  initService,
  TKDDevice,
  newBatteryPack,
  accessoryServiceList,
  handleSubmit,
} = useTKDService(installationPoint);

const suggestedTicketNumbers = computed(
  () => useOrderStore().tkdOrderTicketNumbers
);

const deviceSerialNumber = computed(() => {
  if (device.value) {
    return device.value.serialNumber;
  }
  return '';
});

const { getObjectIdImages, saveImagesToDB } = useImageCapture(
  installationPoint.value?.id
);

const hasTKDService = ref(false);
const images = ref<Image[]>([]);

onBeforeMount(() => {
  if (!props.node) {
    throw new Error('Node is not defined');
  }

  initInstallationPoint(props.parentId, props.node);
  initExistingDevices(props.node);
  initService();

  if (
    installationPoint.value?.id &&
    useOrderStore().hasTKDService(installationPoint.value?.id)
  ) {
    hasTKDService.value = true;
  }

  getObjectIdImages(installationPoint.value?.id).then((imageList) => {
    images.value = imageList;
  });
});

const deviceType = ref(props.payload?.deviceType);

const showReport = computed(() => {
  return useFormStore().currentForm.payload.showReport;
});

const initExistingDevices = (installationPoint: InstallationPoint) => {
  const activeDevice = installationPoint?.getActiveDevice() as DeviceClasses;
  if (activeDevice) {
    deviceType.value = activeDevice?.deviceType;
    initDevice(activeDevice?.deviceType, activeDevice);
  }

  initAccessories();
};

const onSave = () => {
  const result = handleSubmit();
  saveImagesToDB(images.value);
  emit('update-entities', [...result, device.value]);
};

defineExpose({
  onSave,
});

provide('failedInstallationAttempt', failedInstallationAttempt);
</script>
